/**
 * FT-65: Global Vessel Guard
 *
 * Overrides Storage.prototype.setItem to block cross-vessel writes.
 * This catches ALL localStorage writes universally, regardless of the code path.
 *
 * Must be imported at the very top of index.js, BEFORE any other code runs.
 */

// UUID pattern for vessel IDs
const UUID_PATTERN = '[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}';

// Pattern 1: module_orgId_vesselId (org-scoped keys)
// Example: equipment_register_abc123_def456-7890-...
const ORG_SCOPED_PATTERN = new RegExp(`^([a-z_]+)_([a-f0-9-]+)_(${UUID_PATTERN})$`);

// Pattern 2: module_vesselId (simple vessel keys)
// Example: drill_register_abc12345-6789-...
const SIMPLE_VESSEL_PATTERN = new RegExp(`^([a-z_]+)_(${UUID_PATTERN})$`);

// Keys that should NEVER be blocked (system keys)
const EXEMPT_KEYS = [
  'currentVesselId',
  'current_user',
  'vessels',
  'sr.auth',
  'sr.working',
  'sr.feature',
  'sr.migration',
  'ENABLE_',
  'quicklog_',
  'seaready_dev_logs',
  '__private_test__',
];

// FT-168 FIX: Org-scoped modules that use orgId suffix (not vesselId)
// These keys are: module_orgId (no vessel suffix) - they look like vessel keys but aren't
// VesselGuard was incorrectly treating orgId as vesselId and blocking writes
const ORG_SCOPED_PREFIXES = [
  'fleet_crew_',
  'fleet_risk_assessments_',
  'hours_of_rest_',
];

function isOrgScopedKey(key) {
  return ORG_SCOPED_PREFIXES.some(prefix => key.startsWith(prefix));
}

function isExemptKey(key) {
  return EXEMPT_KEYS.some(exempt => key.startsWith(exempt));
}

function extractVesselIdFromKey(key) {
  // Try org-scoped pattern first: module_orgId_vesselId
  let match = key.match(ORG_SCOPED_PATTERN);
  if (match) {
    return match[3]; // vesselId is the third capture group
  }

  // Try simple pattern: module_vesselId
  match = key.match(SIMPLE_VESSEL_PATTERN);
  if (match) {
    return match[2]; // vesselId is the second capture group
  }

  return null;
}

// Store the original setItem
const originalSetItem = Storage.prototype.setItem;

// Override setItem with vessel guard + DIAGNOSTIC TRACE
Storage.prototype.setItem = function(key, value) {
  // Skip non-string keys (shouldn't happen but be safe)
  if (typeof key !== 'string') {
    return originalSetItem.call(this, key, value);
  }

  // FT-355 (POST-TRIAL, deferred): useStorageState({ noScope: true }) is not
  // fully honoured by the storage layer — toOrgScopedKey (src/lib/storage.js)
  // still prepends orgId for user-scoped keys. No active caller as of FT-354d
  // (the FT-354b nav favourites feature was removed), so no targeted exemption
  // is needed here right now. Re-open with FT-355 when a new per-user key
  // consumer is added.

  // Skip exempt keys (system keys that don't need vessel checking)
  if (isExemptKey(key)) {
    return originalSetItem.call(this, key, value);
  }

  // FT-168 FIX: Skip org-scoped keys (these use orgId, not vesselId)
  if (isOrgScopedKey(key)) {
    return originalSetItem.call(this, key, value);
  }

  // Extract vesselId from the key
  const keyVesselId = extractVesselIdFromKey(key);

  // If no vesselId in key, allow the write
  if (!keyVesselId) {
    return originalSetItem.call(this, key, value);
  }

  // Get current vessel ID from localStorage.
  // NOTE: read raw here (not via readCurrentVesselIdRaw helper) because this
  // guard hooks Storage.prototype.setItem and any import that transitively
  // calls setItem during boot risks a re-entrancy loop. The inline
  // strip-quotes normalisation below is sufficient AND self-heals the storage
  // in-place so the next iteration is clean (see FT-365 v3.1 hotfix).
  const currentVesselId = localStorage.getItem('currentVesselId');

  // If no current vessel set yet, allow the write
  // (This happens during initial app load before vessel selection)
  if (!currentVesselId) {
    return originalSetItem.call(this, key, value);
  }

  // Clean currentVesselId (remove quotes if JSON-stringified).
  // FT-365 v3.1 hotfix: also self-heal the LS value so subsequent guards +
  // downstream raw readers see the clean UUID and the warning storm cannot
  // recur even if a legacy double-encoded value is present from a brief
  // FT-365 v3 exposure.
  const cleanCurrentVesselId = currentVesselId.replace(/^"|"$/g, '');
  if (cleanCurrentVesselId !== currentVesselId && cleanCurrentVesselId.length > 0) {
    try {
      // eslint-disable-next-line no-console -- FT-365 v3.1 one-shot observability: fires only on first setItem after a legacy value is seen (self-heal makes subsequent checks clean).
      console.info('[FT-365 v3.1] vesselGuard self-healed double-encoded currentVesselId:', currentVesselId, '→', cleanCurrentVesselId);
      originalSetItem.call(this, 'currentVesselId', cleanCurrentVesselId);
    } catch (_) { /* non-fatal — next read will strip again */ }
  }

  // Compare vessel IDs
  if (keyVesselId !== cleanCurrentVesselId) {
    console.warn(
      `[VESSEL-GUARD] Blocked cross-vessel write: ` +
      `key vesselId=${keyVesselId}, current=${cleanCurrentVesselId}, key=${key}`
    );
    return; // BLOCK the write
  }

  // Vessel matches, allow the write
  return originalSetItem.call(this, key, value);
};
